import { action, observable, makeObservable } from 'mobx';
import { lookupRouteName, sourceOfChange } from 'common/constants';
import {
	MarketplaceOrderEditOrderStep,
	MarketplaceOrderEditOrderLinesStep,
	MarketplaceOrderEditShippingStep,
	MarketplaceOrderEditAssignmentStep,
} from 'modules/order/pages/wizard';
import { ApplicationStore } from 'platform/application/stores';
import { OrderStore } from 'modules/order/stores';
import { LookupStore } from 'modules/lookup/stores';
import { BaseViewStore } from 'core/stores';
import { FormHelperService } from 'common/data';
import { MarketplaceOrderEditForm } from 'modules/order/forms/wizard';
import { ChannelStore } from 'modules/channel/stores';
import {
	notificationError,
	appUserLocalStorageKey,
	formatDate,
	syncDateWithOriginalTime,
	isSuperAdminOrganization,
} from 'core/utils';
import { CarrierServiceStore } from 'modules/carrier/stores';
import { localStorageHandler } from 'core/services';
import { ProductStore } from 'modules/product/stores';
import moment from 'moment';
import _ from 'lodash';
import { BaasicDropdownViewStore } from 'common/stores';
import { ModalParams } from 'core/models';

class MarketplaceOrderEditViewStore extends BaseViewStore {
	@observable orderLineStatusOptions = [];
	@observable countryOptions = [];
	@observable stateOptions = [];
	@observable confirmationTypeOptions = [];
	@observable currencyOptions = [];
	@observable orderTypeOptions = [];
	@observable orderStatusOptions = [];
	@observable channelOptions = [];
	@observable warehouseOptions = [];
	@observable carrierOptions = [];
	@observable carrierServiceOptions = [];
	@observable productOptions = [];
	@observable isCountryLoading = false;
	@observable isOrderStatusLoading = false;
	@observable isOrderTypeLoading = false;
	@observable useEnteredAddress = true;
	@observable config = [
		{
			name: 'orderStep',
			title: 'MARKETPLACEORDEREDITFORM.TITLE.ORDERSTEP',
			component: MarketplaceOrderEditOrderStep,
			isValid: true,
			order: 1,
		},
		{
			name: 'shippingStep',
			title: 'MARKETPLACEORDEREDITFORM.TITLE.SHIPPINGSTEP',
			component: MarketplaceOrderEditShippingStep,
			isValid: true,
			order: 2,
		},
		{
			name: 'orderLinesStep',
			title: 'MARKETPLACEORDEREDITFORM.TITLE.ORDERLINESSTEP',
			component: MarketplaceOrderEditOrderLinesStep,
			isValid: true,
			order: 3,
		},
		{
			name: 'assignmentStep',
			title: 'MARKETPLACEORDEREDITFORM.TITLE.ASSIGNMENTSTEP',
			component: MarketplaceOrderEditAssignmentStep,
			isValid: true,
			order: 4,
		},
	];
	isEdit = false;
	order = null;
	orderDateWithTime = null;
	paymentDateWithTime = null;
	debouncedCountrySearch = null;
	debouncedOrderStatusSearch = null;
	debouncedOrderTypeSearch = null;
	enteredAddress = null;
	updatedAddress = null;
	isAddressVerified = false;
	localStorageKey = localStorageHandler.get(appUserLocalStorageKey)
		? localStorageHandler.get(appUserLocalStorageKey).id + '-order-key'
		: null;

	form = new MarketplaceOrderEditForm({
		onSuccess: async () => {
			await this.validateAddress();
		},
		onError: () => {
			this.checkFormValidity();
			this.rootStore.notificationStore.error('MARKETPLACEORDEREDITFORM.NOTIFICATION.INVALIDFORM');
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.orderStore = new OrderStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.channelStore = new ChannelStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.carrierServiceStore = new CarrierServiceStore(rootStore);
		this.productStore = new ProductStore(rootStore);
		this.formHelperService = new FormHelperService();
		this.isEdit = rootStore.routerStore.routerState.params.id != null ? true : false;
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		this.form.state.options.set({
			validateOnBlur: true,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		let tasks = [];
		if (this.isSuperAdmin) {
			this.sellerDropdownStore = new BaasicDropdownViewStore({
				field: this.form.$('orderStep.seller'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					sellerOnly: true,
				},
				onChangeCallback: this.clearChannelAndSkuData,
			});
			this.form.$('orderStep.seller').set('rules', 'required');
		} else {
			await this.applicationStore.getOrganizationInfo();
			this.sellerId = this.applicationStore.item.id;
			tasks.push(this.getManualOrderChannels());
			tasks.push(this.getProductOptions());
		}
		tasks.push(this.getAssignmentOptions());
		tasks.push(
			this.getLookupOptions([
				'orderStatus',
				'confirmationType',
				'currency',
				'orderType',
				'country',
				'state',
				'orderLineStatus',
			])
		);
		await Promise.all(tasks);

		this.debouncedCountrySearch = _.debounce((searchPhrase, { action }) => {
			this.searchCountryAsync(searchPhrase, { action });
		}, 500);
		this.debouncedOrderStatusSearch = _.debounce((searchPhrase, { action }) => {
			this.searchOrderStatusAsync(searchPhrase, { action });
		}, 500);
		this.debouncedOrderTypeSearch = _.debounce((searchPhrase, { action }) => {
			this.searchOrderTypeAsync(searchPhrase, { action });
		}, 500);
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		this.datePickerFormat = localeData.longDateFormat('L');
		this.setDefaultFormValues();

		if (this.isEdit) {
			await this.getOrder(this.rootStore.routerStore.routerState.params.id, {
				embed: [
					'channel',
					'confirmationType',
					'coreCurrency',
					'orderType',
					'shippingCoreCountryState',
					'shippingCoreCountry',
					'seller',
					'seller.sellerOrganizationInfo',
					'orderLines',
					'orderLines.orderLineStatus',
					'orderStatus',
				],
			});
		} else {
			if (this.isSuperAdmin) {
				const seller = localStorageHandler.get('selectedSeller');
				if (seller) {
					this.sellerDropdownStore.setValue(seller);
					await this.sellerDropdownStore.reload();
				}
			}
			this.addOrderLine();
			this.setDefaultSelectedOptions();
		}
		this.resume();
	};

	@action.bound
	clearChannelAndSkuData = async sellerDropdownValue => {
		if (this.isEdit) {
			this.sellerId = this.order.sellerId;
		} else {
			this.sellerId = sellerDropdownValue.value;
			this.channelOptions = [];
			this.form.$('orderStep.channel').clear();
			this.form.$('orderLinesStep.orderLines').clear();
			this.form.$('assignmentStep.requiredWarehouse').set('value', null);
		}
		await this.getManualOrderChannels();
		await this.getProductOptions();
		await this.getAssignmentOptions();
	};

	@action.bound
	setDefaultFormValues = () => {
		this.form.$('shippingStep.confirmationType').set('value', null);
		this.form.$('shippingStep.currency').set('value', null);
		this.form.$('assignmentStep.requiredCarrierService').set('value', null);
		this.form.$('assignmentStep.requiredWarehouse').set('value', null);
	};

	@action.bound
	async searchOrderTypeAsync(searchPhrase, { action }) {
		if (action === 'input-change') {
			this.isOrderTypeLoading = true;
			this.orderTypeOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.orderType);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.orderTypeOptions = newOptions;
			this.isOrderTypeLoading = false;
		}
	}

	@action.bound
	async searchOrderStatusAsync(searchPhrase, { action }) {
		if (action === 'input-change') {
			this.isOrderStatusLoading = true;
			this.orderStatusOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.orderStatus);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.orderStatusOptions = newOptions;
			this.isOrderStatusLoading = false;
		}
	}

	@action.bound
	async searchCountryAsync(searchPhrase, { action }) {
		if (action === 'input-change') {
			this.isCountryLoading = true;
			this.countryOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.country);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.countryOptions = newOptions;
			this.isCountryLoading = false;
		}
	}

	@action.bound
	async onMenuClose() {
		await this.getLookupOptions(['country', 'orderStatus', 'orderType']);
	}

	@action.bound
	setDefaultSelectedOptions = () => {
		const salesOrderType = _.find(this.orderTypeOptions, item => {
			return item.extra.abrv == 'standardorder';
		});
		if (salesOrderType != null) {
			this.form.$('orderStep.orderType').set('value', salesOrderType);
		}
		const unshippedOrderStatus = _.find(this.orderStatusOptions, item => {
			return item.extra.abrv == 'unshipped';
		});
		if (unshippedOrderStatus != null) {
			this.form.$('orderStep.orderStatus').set('value', unshippedOrderStatus);
		}

		const usdCurrency = _.find(this.currencyOptions, item => {
			return item.extra.code == 'USD';
		});
		if (usdCurrency != null) {
			this.form.$('shippingStep.currency').set('value', usdCurrency);
		}
		const usaCountry = _.find(this.countryOptions, item => {
			return item.extra.abrv == 'US';
		});
		if (usaCountry != null) {
			this.form.$('shippingStep.shippingCoreCountry').set('value', usaCountry);
		}
	};

	@action.bound
	getProductOptions = async () => {
		await this.productStore.findProduct({
			page: 1,
			rpp: 500,
			sellerIds: this.sellerId ? [this.sellerId] : null,
		});
		this.productOptions = _.map(this.productStore.data, item => {
			return { label: item.mainSku, value: item.id, extra: item };
		});
	};

	@action.bound
	getAssignmentOptions = async () => {
		await Promise.all([
			this.applicationStore.find({
				rpp: 100,
				page: 1,
				warehouseOnly: true,
				isOrganizationActive: true,
				includeOnlySellerWarehouses: true,
				sellerWarehousesOnlyOrganizationId: this.sellerId,
			}),
			this.carrierServiceStore.findCarrierService({ page: 1, rpp: 100 }),
		]);
		this.carrierServiceOptions = _.map(this.carrierServiceStore.data, item => {
			return { value: item.name, label: item.name, extra: item };
		});
		this.warehouseOptions = _.map(this.applicationStore.data, item => {
			return { value: item.applicationTitle, label: item.applicationTitle };
		});
	};

	@action.bound
	getManualOrderChannels = async () => {
		await this.channelStore.findChannel({
			page: 1,
			rpp: 15,
			manualOrdersChannelOnly: true,
			sellerIds: this.sellerId ? [this.sellerId] : null,
			embed: [],
		});
		this.channelOptions = _.map(this.channelStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});

		if (this.channelStore.data.length === 0) {
			this.rootStore.notificationStore.error('MARKETPLACEORDEREDITFORM.NOTIFICATION.MISSINGMANUALORDERCHANNEL');
		} else {
			if (this.channelOptions.length > 1) {
				this.form.$('orderStep.channel').set('rules', 'required');
			} else {
				this.form.$('orderStep.channel').set('rules', '');
			}
		}
	};

	@action.bound
	getOrder = async (id, options) => {
		await this.orderStore.getOrder(id, options);
		this.order = this.orderStore.item;
		await this.setFormValues();
	};

	@action.bound
	setFormValues = async () => {
		if (this.isSuperAdmin) {
			this.sellerDropdownStore.setValue({ value: this.order.sellerId });
			await this.sellerDropdownStore.reload();
		}

		// save date with time
		//orderDate
		this.orderDateWithTime = formatDate(this.order.orderDate, true); // initial date, received in UTC
		this.order.orderDate = formatDate(this.order.orderDate, true, this.datePickerFormat); // to get the same date as on ListView

		//paymentDate
		this.paymentDateWithTime = formatDate(this.order.paymentDate, true);
		this.order.paymentDate = formatDate(this.order.paymentDate, true, this.datePickerFormat);

		//shipByDate - no conversions
		this.order.shipByDate = formatDate(this.order.shipByDate, false, this.datePickerFormat);

		//deliverByDate - no conversions
		if (this.order.deliverByDate != null) {
			this.order.deliverByDate = formatDate(this.order.deliverByDate, false, this.datePickerFormat);
		}

		this.formHelperService.updateForm(this.form, this.order, false);
		this.form
			.$('orderStep.orderType')
			.set('value', { value: this.order.orderType.id, label: this.order.orderType.name });
		this.form
			.$('orderStep.orderStatus')
			.set('value', { value: this.order.orderStatus.id, label: this.order.orderStatus.name });
		this.form.$('orderStep.channel').set('value', { value: this.order.channel.id, label: this.order.channel.name });
		this.form
			.$('shippingStep.shippingCoreCountry')
			.set('value', { value: this.order.shippingCoreCountry.id, label: this.order.shippingCoreCountry.name });
		this.form
			.$('shippingStep.shippingCoreCountryState')
			.set('value', { value: this.order.shippingCoreCountryState.id, label: this.order.shippingCoreCountryState.name });
		if (this.order.confirmationType != null) {
			this.form
				.$('shippingStep.confirmationType')
				.set('value', { value: this.order.confirmationType.id, label: this.order.confirmationType.name });
		}
		if (this.order.coreCurrency != null) {
			this.form
				.$('shippingStep.currency')
				.set('value', { value: this.order.coreCurrency.id, label: this.order.coreCurrency.currency });
		}
		if (this.order.requiredWarehouse != '') {
			this.form
				.$('assignmentStep.requiredWarehouse')
				.set('value', { value: this.order.requiredWarehouse, label: this.order.requiredWarehouse });
		}
		if (this.order.requiredCarrierService != '') {
			const selectedCarrierServiceOption = _.find(this.carrierServiceOptions, item => {
				return item.extra.name == this.order.requiredCarrierService;
			});
			if (selectedCarrierServiceOption != null) {
				this.form.$('assignmentStep.requiredCarrierService').set('value', {
					value: selectedCarrierServiceOption.extra.name,
					label: `${selectedCarrierServiceOption.extra.name}`,
					extra: selectedCarrierServiceOption.extra,
				});
			}
		}
		_.each(this.order.orderLines, item => {
			const productOption = _.find(this.productOptions, product => {
				return item.productId == product.value;
			});
			this.form.$('orderLinesStep.orderLines').add([
				{
					sellerId: item.sellerId,
					product: productOption,
					quantity: item.quantity,
					id: item.id,
					unitPrice: item.unitPrice,
					taxPaid: item.taxPaid,
					productId: item.productId,
					orderLineStatus: { value: item.orderLineStatus.id, label: item.orderLineStatus.name },
				},
			]);
			this.productOptions = _.remove(this.productOptions, item => {
				return !(item.value == productOption.value);
			});
		});
	};

	@action.bound
	getLookupOptions = async lookups => {
		for await (var lookup of lookups) {
			await this.lookupStore.find({ page: 1, rpp: 250 }, lookupRouteName[lookup]);
			this[`${lookup}Options`] = _.map(this.lookupStore.data, item => {
				return { label: item.currency != null ? item.currency : item.name, value: item.id, extra: item };
			});
		}
	};

	@action.bound
	addOrderLine = () => {
		this.form.$('orderLinesStep.orderLines').add([
			{
				product: null,
				quantity: '',
				id: '',
				unitPrice: '',
				taxPaid: '',
				orderLineStatus: null,
			},
		]);
	};

	@action.bound
	onDelete = (evt, field) => {
		this.form.$(field.path).onClear(evt);
		this.form.$(field.path).onDel(evt);
	};

	@action.bound
	onSelectChange = (value, field, excludeOptions = false) => {
		if (excludeOptions) {
			this.excludeProductOptions(value, field);
		}
		if (field.path == 'orderStep.channel') {
			const unshippedOrderStatus = _.find(this.orderStatusOptions, item => {
				return item.extra.abrv == 'unshipped';
			});
			if (unshippedOrderStatus != null) {
				this.form.$('orderStep.orderStatus').set('value', unshippedOrderStatus);
			}
		}
		this.form.$(field.path).set('value', value);
	};

	@action.bound
	excludeProductOptions = (value, field) => {
		if (field.value != null) {
			this.productOptions.push(field.value);
		}
		const idxToRemove = _.findIndex(this.productOptions, item => {
			return item.value === value.value;
		});
		if (idxToRemove > -1) {
			this.productOptions.splice(idxToRemove, 1);
		}
	};

	@action.bound
	checkFormValidity = () => {
		_.each(this.config, step => {
			if (this.form.has(`${step.name}`)) {
				this.form
					.$(`${step.name}`)
					.validate()
					.then(({ isValid }) => {
						if (!isValid) {
							step.isValid = isValid;
						} else {
							step.isValid = isValid;
						}
					});
			}
		});
		if (this.channelStore.data.length === 0) {
			this.rootStore.notificationStore.error('MARKETPLACEORDEREDITFORM.NOTIFICATION.MISSINGMANUALORDERCHANNEL');
		}
		this.form.invalidate();
		this.form.showErrors();
	};

	// Validate individual step.
	@action.bound
	async checkValidityForStep(stepNumber) {
		_.each(this.config, step => {
			if (step.order == stepNumber) {
				if (this.form.has(`${step.name}`)) {
					this.form
						.$(`${step.name}`)
						.validate()
						.then(({ isValid }) => {
							if (!isValid) {
								step.isValid = isValid;
							} else {
								step.isValid = isValid;
							}
						});
				}
			}
			if (!step.isValid) {
				this.form.invalidate();
				this.form.showErrors();
			}
		});
	}

	@action.bound
	validateAddress = async () => {
		this.suspend();
		try {
			if (this.userHasUpdatedTheAddress()) {
				let orderDetails = this.initializeOrderDetails();
				const addressValidationResponse = await this.orderStore.validateOrderAddress(orderDetails);
				this.isAddressVerified = addressValidationResponse.isAddressVerified;
				orderDetails.isAddressVerified = addressValidationResponse.isAddressVerified;
				orderDetails.addressChanged = addressValidationResponse.addressChanged;

				// If Postgrid updated the address, open address validation modal for user to choose the correct address.
				if (addressValidationResponse.enteredAddress != null && addressValidationResponse.updatedAddress != null) {
					this.openValidationAddressModal();
					this.enteredAddress = addressValidationResponse.enteredAddress;
					this.updatedAddress = addressValidationResponse.updatedAddress;
				} else {
					this.createOrUpdateOrder();
				}
			} else {
				this.createOrUpdateOrder();
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	createOrUpdateOrder = async () => {
		this.suspend();
		try {
			const order = this.initializeOrder(this.form.values());
			if (this.isEdit) {
				order.orderDate = syncDateWithOriginalTime(this.orderDateWithTime, order.orderDate).toISOString();
				order.paymentDate = syncDateWithOriginalTime(this.paymentDateWithTime, order.paymentDate).toISOString();
				order.sellerId = this.order.sellerId;
				await this.orderStore.updateOrder(order, sourceOfChange.updateOrder);
				this.rootStore.notificationStore.success([
					'MARKETPLACEORDEREDITFORM.NOTIFICATION.UPDATE',
					{ orderNumber: order.orderNumber },
				]);
			} else {
				order.orderDate = new Date(order.orderDate).toISOString();
				order.paymentDate = new Date(order.paymentDate).toISOString();
				if (this.isSuperAdmin) {
					order.sellerId = this.sellerId;
				}
				await this.orderStore.createOrder(order, sourceOfChange.createOrder);
				localStorageHandler.remove(this.localStorageKey);
				this.rootStore.notificationStore.success([
					'MARKETPLACEORDEREDITFORM.NOTIFICATION.CREATE',
					{ orderNumber: order.orderNumber },
				]);
			}
			let lastOrganization = localStorageHandler.get('lastOrganization');
			const ordersellerId = this.isSuperAdmin ? order.sellerId : lastOrganization != null ? lastOrganization.id : null;
			if (ordersellerId != null && ordersellerId != '') {
				await this.applicationStore.getApplication(ordersellerId, {
					embed: ['sellerOrganizationInfo'],
				});

				if (
					!this.applicationStore.item.sellerOrganizationInfo.partiallyFulfillMixedOrders &&
					order.orderLines.some(ol => !ol.isProductActive)
				) {
					this.rootStore.notificationStore.warning([
						'ORDERVIEW.PARTIALLYFULFILLMIXEDORDERS',
						{ orderId: order.orderNumber },
					]);
				}
			}

			this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list');
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	handleBackButtonClick = () => {
		this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list');
	};

	initializeOrder = ({ orderStep, shippingStep, orderLinesStep, assignmentStep }) => {
		if (!this.useEnteredAddress) {
			shippingStep.shippingAddress1 = this.updatedAddress.streetLines[0] ? this.updatedAddress.streetLines[0] : '';
			shippingStep.shippingAddress2 = this.updatedAddress.streetLines[1] ? this.updatedAddress.streetLines[1] : '';
			shippingStep.shippingCity = this.updatedAddress.city;
			shippingStep.shippingZipCode = this.updatedAddress.postalCode;
		}
		const orderLines = _.map(orderLinesStep.orderLines, orderLine => {
			return {
				id: orderLine.id,
				sku: orderLine.product.extra.mainSku,
				isProductActive: orderLine.product.extra.isActive,
				productId: orderLine.product.value,
				quantity: orderLine.quantity,
				unitPrice: parseFloat(orderLine.unitPrice),
				taxPaid: parseFloat(orderLine.taxPaid),
				orderLineStatusId: orderLine.orderLineStatus.value,
				sellerId: orderLine.sellerId,
			};
		});

		let order = {
			...orderStep,
			...shippingStep,
			addressChanged: this.useEnteredAddress ? false : true,
			isAddressVerified: this.isAddressVerified,
			orderStatusId: orderStep.orderStatus.value,
			orderTypeId: orderStep.orderType.value,
			shippingCoreCountryId: shippingStep.shippingCoreCountry.value,
			shippingCoreCountryStateId: shippingStep.shippingCoreCountryState.value,
			confirmationTypeId: shippingStep.confirmationType != null ? shippingStep.confirmationType.value : null,
			currencyId: shippingStep.currency != null ? shippingStep.currency.value : null,
			channelId:
				orderStep.channel != null && orderStep.channel != '' ? orderStep.channel.value : this.channelOptions[0].value,
			requiredCarrierService:
				assignmentStep.requiredCarrierService != null ? assignmentStep.requiredCarrierService.value : null,
			requiredWarehouse: assignmentStep.requiredWarehouse != null ? assignmentStep.requiredWarehouse.value : null,
			orderLines,
		};

		// Remove unused Order properties.
		delete order.useEnteredAddress;
		delete order.useUpdatedAddress;
		delete order.currency;

		return order;
	};

	addressValidationModal = new ModalParams({});

	@action.bound
	openValidationAddressModal(item) {
		this.selectedItem = item;
		this.addressValidationModal.open();
	}

	@action.bound
	async onValidateAddressBack() {
		this.useEnteredAddress = true;
		this.addressValidationModal.close();
	}

	@action.bound
	onToggleAddress(useEnteredAddress) {
		this.useEnteredAddress = useEnteredAddress;
	}

	@action.bound
	async onValidateAddressConfirm() {
		await this.createOrUpdateOrder();
	}

	dateTimeFormatMonthToUpperCase(string) {
		return string.charAt(0).toUpperCase() + string.charAt(1).toUpperCase() + string.slice(2);
	}

	@action.bound
	userHasUpdatedTheAddress() {
		if (!this.isEdit) {
			return true;
		} else {
			const { shippingStep } = this.form.values();

			return (
				this.order.shippingAddress1 !== shippingStep.shippingAddress1 ||
				this.order.shippingAddress2 !== shippingStep.shippingAddress2 ||
				this.order.shippingCity !== shippingStep.shippingCity ||
				this.order.shippingZipCode !== shippingStep.shippingZipCode ||
				this.order.shippingCoreCountry.id !== shippingStep.shippingCoreCountry.value ||
				this.order.shippingCoreCountryState.id !== shippingStep.shippingCoreCountryState.value
			);
		}
	}

	@action.bound
	initializeOrderDetails() {
		const { shippingStep, orderStep } = this.form.values();

		return {
			id: this.isEdit ? this.order.id : undefined,
			shippingAddress1: shippingStep.shippingAddress1,
			shippingAddress2: shippingStep.shippingAddress2,
			shippingCity: shippingStep.shippingCity,
			shippingZipCode: shippingStep.shippingZipCode,
			buyerPhone: shippingStep.buyerPhone,
			buyerName: shippingStep.buyerName,
			shippingCoreCountryId: shippingStep.shippingCoreCountry.value,
			shippingCoreCountryStateId: shippingStep.shippingCoreCountryState.value,
			shipByDate: orderStep.shipByDate,
			dateFormat: this.dateTimeFormatMonthToUpperCase(this.datePickerFormat.toLowerCase()),
		};
	}
}

export default MarketplaceOrderEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/wizard/MarketplaceOrderEditViewStore.js